import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, MapPin, ChevronRight, Loader2 } from "lucide-react";
import { useI18n } from '@/hooks/useI18n';
import { ActivityType } from "../models/activity.model";
import { useActivityLibraryQuery } from "@/lib/library/hooks/useActivityLibrary";
import { LibraryActivity } from "@/lib/library/models/library-activity.model";

interface LibraryPickerProps {
  /** Tipo di attività su cui filtrare la libreria. */
  activityType: ActivityType;
  /** La query parte solo quando il picker è davvero visibile. */
  enabled?: boolean;
  onSelect: (activity: LibraryActivity) => void;
  /** Altezza massima della lista: cambia fra modale dedicata e tab. */
  listClassName?: string;
}

/**
 * Ricerca e scelta di un'attività dalla libreria personale/di agenzia.
 *
 * È il contenuto del tab "Seleziona dalla libreria" delle modali di creazione:
 * si cerca, si sceglie, si conferma e i campi finiscono nel form.
 */
export const LibraryPicker = ({
  activityType,
  enabled = true,
  onSelect,
  listClassName = 'max-h-[50vh]',
}: LibraryPickerProps) => {
  const { t } = useI18n();
  const [search, setSearch] = useState('');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);

  // Filtri puliti ogni volta che il picker sparisce, per non ritrovare uno stato
  // "sporco" alla riapertura su un tipo diverso.
  useEffect(() => {
    if (!enabled) {
      setSearch('');
      setSelectedTag(null);
    }
  }, [enabled]);

  // La query è filtrata server-side per tipo attività: ogni tipo ha la sua query key
  // (e la sua cache), così non si scarica mai l'intera libreria. Ricerca e tag
  // restano filtri client-side sul sottoinsieme del tipo corrente.
  const { data: activitiesForType = [], isLoading } = useActivityLibraryQuery(
    { activityType },
    { enabled }
  );

  // Replica client-side lo stesso match testuale della RPC get_activity_library
  // (ILIKE case-insensitive su title/description/address).
  // Nota: `location` NON è un indirizzo in questo modello — è l'indice d'ordine
  // dell'attività nel giorno — quindi il campo cercato come "luogo" è `address`.
  const searchedActivities = useMemo(() => {
    const searchLower = search.trim().toLowerCase();
    if (!searchLower) return activitiesForType;
    return activitiesForType.filter(
      (activity) =>
        activity.title.toLowerCase().includes(searchLower) ||
        (activity.description ?? '').toLowerCase().includes(searchLower) ||
        (activity.address ?? '').toLowerCase().includes(searchLower)
    );
  }, [activitiesForType, search]);

  const availableTags = useMemo(() => {
    const tagSet = new Set<string>();
    searchedActivities.forEach((activity) => {
      activity.tags.forEach((tag) => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [searchedActivities]);

  const filteredActivities = useMemo(() => {
    if (!selectedTag) return searchedActivities;
    return searchedActivities.filter((activity) => activity.tags.includes(selectedTag));
  }, [searchedActivities, selectedTag]);

  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('library.pickerSearchPlaceholder')}
          className="h-9 pl-9"
        />
      </div>

      {availableTags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setSelectedTag(null)}
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
              !selectedTag
                ? 'bg-gray-900 text-white border-gray-900'
                : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {t('library.pickerAllTags')}
          </button>
          {availableTags.map((tag) => (
            <button
              key={tag}
              type="button"
              onClick={() => setSelectedTag(tag)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                selectedTag === tag
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {tag}
            </button>
          ))}
        </div>
      )}

      <div className={`flex flex-col gap-2 overflow-y-auto ${listClassName}`}>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
          </div>
        ) : filteredActivities.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">
            {t('library.pickerEmptyState')}
          </p>
        ) : (
          filteredActivities.map((activity) => (
            <button
              key={activity.id}
              type="button"
              onClick={() => onSelect(activity)}
              className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 text-left w-full transition-colors hover:bg-gray-50"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-gray-900 truncate">{activity.title}</p>
                  {activity.agency_id !== null && (
                    <Badge variant="secondary">{t('library.pickerAgencyBadge')}</Badge>
                  )}
                </div>
                {activity.address && (
                  <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-1 truncate">
                    <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate">{activity.address}</span>
                  </p>
                )}
                {activity.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {activity.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs text-gray-500 bg-gray-100 rounded px-1.5 py-0.5"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
            </button>
          ))
        )}
      </div>
    </div>
  );
};

export default LibraryPicker;
